import { formatDay } from "@/lib/hooks";
import type { ValueState } from "@/types";

/**
 * Where a number came from, in the same micro-type as "target 15.5–17.5" and
 * "judged on 96 carbonate".
 *
 * Provenance is a third thing a row can say about a figure, not a third *way*
 * of saying things — so it reuses the qualifier line the row already has rather
 * than arriving with a badge, a colour and a legend. Four states on a phone at a
 * railing is already at the edge of what a glance can carry.
 *
 * Only two of the four states print anything here, and that is the restraint
 * that keeps the common case identical to before:
 *
 * - **measured** — silent. Nothing to explain; this is what a reading is.
 * - **carried** — the source date, which is precisely what ADR 0010 asks for
 *   ("carrying adds a source date rather than a new vocabulary"). The value is
 *   real and still judged against its target, so nothing else about the row
 *   changes.
 * - **superseded** — words, not colour. "changed since testing" survives direct
 *   sunlight, a greyscale screenshot and a colour-blind reader, none of which a
 *   tinted chip does. The detail — what went in, when, and where it should have
 *   landed — is in the note underneath, written once by the server.
 * - **missing** — silent. The em dash and the dashed comparator bar already say
 *   it, and have since before any of this existed.
 */
export default function ProvenanceLine({
  state,
  measuredAt,
}: {
  state: ValueState;
  measuredAt?: string | null;
}) {
  if (state === "carried") {
    return (
      <p className="unit mt-0.5">
        carried{measuredAt ? ` · tested ${formatDay(measuredAt)}` : " forward"}
      </p>
    );
  }

  if (state === "superseded") {
    return <p className="unit mt-0.5">changed since testing</p>;
  }

  return null;
}
